/**
 * Ritmo, frequência cardíaca e altimetria ao longo da distância (SVG próprio, sem biblioteca).
 * O ritmo é suavizado numa janela de ~30 s e o eixo é invertido: mais rápido fica em cima, como os
 * corredores leem. Cada medida tem o seu gráfico (nunca dois eixos Y no mesmo gráfico).
 */
import { svgScaleStyle, useSvgScale } from "../lib/useSvgScale";
import type { ActivityStream } from "../api/types";
import { formatPace } from "../lib/format";
import { LOCALE, t } from "../i18n";

const W = 640;
const H = 150;
const PAD = { l: 52, r: 12, t: 10, b: 24 };

interface Series {
  x: number[];
  y: number[];
}

function pacePoints(s: ActivityStream["data"]): Series {
  const tp = s.t;
  const d = s.d ?? [];
  const out: Series = { x: [], y: [] };
  let j = 0;
  for (let i = 1; i < tp.length; i++) {
    const di = d[i];
    if (di == null) continue;
    while (j < i - 1 && tp[i] - tp[j + 1] >= 30) j++;
    const dj = d[j];
    if (dj == null) continue;
    const dd = di - dj;
    const dt = tp[i] - tp[j];
    if (dd > 5 && dt > 0) {
      const pace = (dt / dd) * 1000;
      if (pace >= 150 && pace <= 900) {
        out.x.push(di / 1000);
        out.y.push(pace);
      }
    }
  }
  return out;
}

function hrPoints(s: ActivityStream["data"]): Series {
  const out: Series = { x: [], y: [] };
  const d = s.d ?? [];
  (s.hr ?? []).forEach((h, i) => {
    if (h && d[i] != null) {
      out.x.push((d[i] as number) / 1000);
      out.y.push(h);
    }
  });
  return out;
}

function altPoints(s: ActivityStream["data"]): Series {
  const out: Series = { x: [], y: [] };
  const d = s.d ?? [];
  (s.alt ?? []).forEach((a, i) => {
    if (a != null && d[i] != null) {
      out.x.push((d[i] as number) / 1000);
      out.y.push(a);
    }
  });
  return out;
}

function niceTicks(min: number, max: number, count: number): number[] {
  const span = max - min || 1;
  const raw = span / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? raw;
  const out: number[] = [];
  for (let v = Math.ceil(min / step) * step; v <= max + 1e-9; v += step) out.push(+v.toFixed(6));
  return out;
}

function Chart({
  series,
  label,
  invert,
  fmt,
  tone,
  maxX,
}: {
  series: Series;
  label: string;
  invert?: boolean;
  fmt: (v: number) => string;
  tone: string;
  maxX: number;
}) {
  const [svgRef, k] = useSvgScale(W);   // textos com 12px na tela mesmo com o gráfico encolhido
  const ys = [...series.y].sort((a, b) => a - b);
  // corta os 2 % extremos (GPS ruidoso) para a escala não ser dominada por um ponto
  const lo = ys[Math.floor(ys.length * 0.02)] ?? 0;
  const hi = ys[Math.ceil(ys.length * 0.98) - 1] ?? 1;
  const pad = (hi - lo) * 0.08 || 5;
  const yMin = lo - pad;
  const yMax = hi + pad;
  const yTicks = niceTicks(yMin, yMax, 3).filter((v) => v >= yMin && v <= yMax);
  // o texto do eixo Y cresce com `k`: a margem esquerda acompanha para o rótulo não sair da área do gráfico
  const L = Math.min(160, Math.max(PAD.l, Math.ceil(Math.max(0, ...yTicks.map((v) => fmt(v).length)) * 7.4 * k) + 12));
  const sx = (x: number) => L + (x / (maxX || 1)) * (W - L - PAD.r);
  const sy = (y: number) => {
    const clamped = Math.min(yMax, Math.max(yMin, y));
    const f = (clamped - yMin) / (yMax - yMin || 1);
    return invert ? PAD.t + f * (H - PAD.t - PAD.b) : H - PAD.b - f * (H - PAD.t - PAD.b);
  };
  const path = series.x.map((x, i) => `${i ? "L" : "M"}${sx(x).toFixed(1)},${sy(series.y[i]).toFixed(1)}`).join("");
  const xTicks = niceTicks(0, maxX, 6).filter((v) => v <= maxX);
  return (
    <figure className="chart">
      <figcaption>{label}</figcaption>
      <svg ref={svgRef} style={svgScaleStyle(k)} viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label}>
        {yTicks.map((v) => (
          <g key={v}>
            <line x1={L} x2={W - PAD.r} y1={sy(v)} y2={sy(v)} className="grid" />
            <text x={L - 6} y={sy(v) + 4} textAnchor="end" className="tick">
              {fmt(v)}
            </text>
          </g>
        ))}
        {xTicks.map((v) => (
          <text key={v} x={sx(v)} y={H - 6} textAnchor="middle" className="tick">
            {t("{v} km", { v: v.toLocaleString(LOCALE) })}
          </text>
        ))}
        <path d={path} fill="none" stroke={tone} strokeWidth={1.8} strokeLinejoin="round" />
      </svg>
    </figure>
  );
}

export function ActivityChart({ stream }: { stream: ActivityStream }) {
  const pace = pacePoints(stream.data);
  const hr = hrPoints(stream.data);
  const alt = altPoints(stream.data);
  const altRange = alt.y.length ? Math.max(...alt.y) - Math.min(...alt.y) : 0;
  const maxX = Math.max(pace.x[pace.x.length - 1] ?? 0, hr.x[hr.x.length - 1] ?? 0);
  if (pace.x.length < 5 && hr.x.length < 5) return <p className="muted">{t("Sem séries suficientes para o gráfico.")}</p>;
  return (
    <div className="stack">
      {pace.x.length >= 5 && (
        <Chart series={pace} label={t("Ritmo (min/km)")} invert maxX={maxX} tone="var(--primary)" fmt={(v) => formatPace(v).replace(" /km", "")} />
      )}
      {hr.x.length >= 5 && <Chart series={hr} label={t("Frequência cardíaca (bpm)")} maxX={maxX} tone="var(--danger)" fmt={(v) => String(Math.round(v))} />}
      {alt.x.length >= 5 && altRange >= 3 && (
        <Chart series={alt} label={t("Altimetria (m)")} maxX={maxX} tone="var(--muted)" fmt={(v) => String(Math.round(v))} />
      )}
      <p className="muted" style={{ fontSize: "var(--step--1)" }}>
        {t("{sample_count} de {original_count} pontos do arquivo exibidos.", { sample_count: stream.sample_count, original_count: stream.original_count })}{" "}</p>
    </div>
  );
}
